/**
 * Styles for `CameraSnapshotsOverlay` — the snapshot filmstrip pinned to the left edge of
 * the camera stream widget, and the full-size viewer it opens.
 */

import styled from 'styled-components';

import { colors, responsive } from '@/utils';

import type { SnapshotPersistState } from './useVideoSnapshots.ts';

/* Hidden by default; revealed only while the pointer is over the strip's own left-edge
   area (NOT the whole widget — pointer-events stay on so the invisible strip is its own
   hover hot-zone). Clicks on it are stopPropagation'd either way, so an invisible strip
   never triggers widget surface actions. */
export const StyledFilmstripHost = styled.div<{ $zIndex: number }>`
  position: absolute;
  left: ${responsive.spacing.large};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: ${responsive.spacing.small};
  max-height: 70%;
  z-index: ${({ $zIndex }) => $zIndex};
  pointer-events: auto;
  opacity: 0;
  transition: opacity 0.25s ease-in-out;

  &:hover,
  &:focus-within {
    opacity: 1;
  }
`;

export const StyledThumbColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${responsive.spacing.small};
  overflow-y: auto;
  min-height: 0;
`;

export const StyledThumbButton = styled.button`
  position: relative;
  padding: 0;
  border: 1px solid ${colors.background.transparent[200]};
  border-radius: ${responsive.radius.small};
  overflow: hidden;
  width: 6.75rem;
  cursor: pointer;
  background: none;
  line-height: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  flex-shrink: 0;

  img {
    display: block;
    width: 100%;
  }

  &:hover {
    border-color: ${colors.icon.action[100]};
  }
`;

/** Corner dot on thumbs that are not stored on-cluster:
    amber = uploading · red = session-only, the storage call failed. */
export const StyledPersistBadge = styled.span<{ $state: SnapshotPersistState }>`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $state }) => ($state === 'saving' ? '#f0c040' : '#e05050')};
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.8);
`;

/** Time slider across ALL stored snapshots for the camera, both tiers (right = latest). */
export const StyledHistorySliderBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 6.75rem;
  flex-shrink: 0;
`;

export const StyledHistorySlider = styled.input`
  width: 100%;
  height: 4px;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 999px;
  background: ${colors.background.transparent[200]};
  cursor: pointer;
  accent-color: ${colors.icon.action[100]};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  }
  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border: none;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  }
`;

export const StyledHistoryLabel = styled.span`
  color: white;
  font-size: 0.65rem;
  font-variant-numeric: tabular-nums;
  text-align: center;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

export const StyledViewerOverlay = styled.div<{ $zIndex: number }>`
  position: absolute;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
`;

export const StyledViewerImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

export const StyledViewerActions = styled.div`
  position: absolute;
  top: ${responsive.spacing.medium};
  right: ${responsive.spacing.medium};
  display: flex;
  gap: ${responsive.spacing.small};
`;

export const StyledViewerCaption = styled.span`
  position: absolute;
  bottom: ${responsive.spacing.medium};
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;
